import { useSeekaConverge } from "@seeka-labs/converge-react"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"
import debounce from "lodash.debounce"
import React from "react"
import { StoreContext } from "../context/store-context"
import DeleteIcon from "../icons/delete"
import { formatPrice } from "../utils/format-price"
import { DarkButton } from "./buttons/dark-button"
import {
  container,
  cstLabel,
  formatter,
  linethrough,
  listingContainerStyle,
  miniImage,
  PriceList,
  remove,
  subTotal,
  twoColPrice,
  variant
} from "./line-item-mini-cart.module.css"
import { NumericInput } from "./numeric-input"

export default function LineItemMiniCart({ item, vendor, subheading }) {
  const converge = useSeekaConverge()
  const { removeLineItem, cart, updateLineItem, loading } =
    React.useContext(StoreContext)
  const [quantity, setQuantity] = React.useState(item.quantity)

  const compareAtPrice = item.merchandise.compareAtPriceV2
    ? formatPrice(
      item.merchandise.compareAtPriceV2.currencyCode,
      Number(item.merchandise.compareAtPriceV2.amount) * quantity
    )
    : null

  const subtotal = formatPrice(
    item.merchandise.priceV2.currencyCode,
    Number(item.merchandise.priceV2.amount) * quantity
  )

  const handleRemove = () => {
    if (converge) {
      converge.track.removeFromCart({
        currencyCode: "SGD",
        productIdentifier: item.merchandise.product.id,
        variantIdentifier: item.merchandise.id,
        sku: item.merchandise.sku,
        lineItemIdentifier: item.id,
        productName: item.merchandise.product.title,
        // variantName: item.merchandise.product.title, // Uncomment if variant title is necessary
        // categoryName: '', // Add category name if available
        brandName: vendor,
        unitPrice: item.merchandise.priceV2.amount,
        quantity: quantity,
        totalDiscount: 0,
      })
    }
    removeLineItem(cart.id, item.id)
  }

  const uli = debounce(
    (value) => updateLineItem(cart.id, item.id, value),
    300
  )
  const debouncedUli = React.useCallback((value) => uli(value), [])

  const handleQuantityChange = (value) => {
    if (value !== "" && Number(value) < 1) {
      return
    }
    setQuantity(value)
    if (Number(value) >= 1) {
      debouncedUli(value)
    }
  }

  function doIncrement() {
    handleQuantityChange(Number(quantity || 0) + 1)
    if (converge) {
      converge.track
        .addToCart({
          currencyCode: "SGD",
          productIdentifier: item.merchandise.product.id,
          variantIdentifier: item.merchandise.id,
          sku: item.merchandise.sku,
          productName: item.merchandise.product.title,
          // categoryName: categoryName,
          brandName: vendor,
          unitPrice: item.merchandise.priceV2.amount,
          quantity: 1,
          totalInventory: item.merchandise.totalInventory || 0,
          totalDiscount: 0,
          lineItemIdentifier: item.id, // You might need to replace this with an actual line item identifier if available
        })
        .catch((error) => {
          console.error("Error adding to cart:", error)
        })
    }
  }

  function doDecrement() {
    handleQuantityChange(Number(quantity || 0) - 1)
    if (converge) {
      converge.track
        .removeFromCart({
          currencyCode: "SGD",
          productIdentifier: item.merchandise.product.id,
          variantIdentifier: item.merchandise.id,
          sku: item.merchandise.sku,
          productName: item.merchandise.product.title,
          // categoryName: categoryName,
          brandName: vendor,
          unitPrice: item.merchandise.priceV2.amount,
          quantity: 1,
          totalInventory: item.merchandise.totalInventory || 0,
          totalDiscount: 0,
          lineItemIdentifier: item.id, // You might need to replace this with an actual line item identifier if available
        })
        .catch((error) => {
          console.error("Error removing from cart:", error)
        })
    }
  }

  const image = React.useMemo(() => {
    const variantImage = {
      ...item.merchandise.image,
      originalSrc: item.merchandise.image.src,
    }

    if (variantImage && variantImage.src) {
      let width = 200 // Default width for non-mobile
      let height = 200 // Default height for non-mobile

      if (typeof window !== "undefined" && window.innerWidth < 800) {
        // Adjust width and height for mobile devices
        width = window.innerWidth - 20 // Example adjustment, you can set it according to your layout
        height = width // Maintain aspect ratio
      }

      return getShopifyImage({
        image: variantImage,
        layout: "constrained",
        crop: "contain",
        width: width,
        height: height,
      })
    } else {
      return null
    }
  }, [item.merchandise.image])

  return (
    <>
      <div className={listingContainerStyle}>
        {image && (
          <GatsbyImage
            key={image.url}
            image={image}
            alt={image.altText ?? item.merchandise.product.title}
            className={miniImage}
          />
        )}
        <div className={container}>
          <h3 className={formatter}
          >
            {item.merchandise.product.title}
          </h3>
          <p className={formatter}>{vendor}</p>
          {subheading && <p className={formatter}>{subheading}</p>}
          <h4 className={variant}>
            {item.merchandise.title === "Default Title" ? "" : item.merchandise.product.title}
          </h4>
          <div className={twoColPrice}>
            <div className={twoColPrice}>
              <NumericInput
                disabled={loading}
                value={quantity}
                product={item.merchandise.product}
                aria-label="Quantity"
                onIncrement={doIncrement}
                onDecrement={doDecrement}
                onChange={(e) => handleQuantityChange(e.currentTarget.value)}
              />
            </div>
            <div className={twoColPrice}>
              <div className={PriceList}>
                <span className={cstLabel}>
                  {compareAtPrice && (
                    <>
                      Was{" "}
                      <span className={subTotal}>
                        <span className={linethrough}>
                          ${compareAtPrice.toString().split(".")[0]} SGD
                        </span>
                        <br />
                      </span>
                    </>
                  )}
                  ${subtotal.split(".")[0]} SGD
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={remove}>
        <DarkButton onClick={handleRemove}>
          <DeleteIcon />
        </DarkButton>
      </div>
      <br />
      <hr />
      <br />
    </>
  )
}
